'use client';

/**
 * RAG / rerank feed renderer. Shows the most recent query + its ranked chunks
 * (id · doc · score · text) as a compact table at the top, then the full raw
 * event stream below via JsonLogRenderer.
 */
import React, { useMemo, useState } from 'react';
import { JsonLogRenderer, type FeedRendererProps } from './JsonLogRenderer';
import type { AgentDebugEvent } from '@/lib/debug/types';

interface ChunkRow {
  chunk_id?: unknown;
  doc?: unknown;
  score?: unknown;
  rerank_score?: unknown;
  avg_pool_score?: unknown;
  text?: unknown;
}

function asStr(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}
function asNum(v: unknown): string {
  if (typeof v === 'number') return v.toFixed(3);
  return v == null ? '' : String(v);
}

function latestChunkEvent(events: AgentDebugEvent[]): AgentDebugEvent | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const k = events[i].kind;
    if (k === 'rag.chunks' || k === 'rerank.out' || k === 'rag.query' || k === 'rerank.in') return events[i];
  }
  return null;
}

function extractChunks(ev: AgentDebugEvent | null): { query: string; rows: ChunkRow[] } {
  if (!ev || typeof ev.payload !== 'object' || ev.payload == null) return { query: '', rows: [] };
  const p = ev.payload as Record<string, unknown>;
  const query = asStr(p.query);
  const arr =
    (Array.isArray(p.chunks) && p.chunks) ||
    (Array.isArray(p.ranked) && p.ranked) ||
    (Array.isArray(p.candidates) && p.candidates) ||
    [];
  return { query, rows: arr.filter((x) => x && typeof x === 'object') as ChunkRow[] };
}

export function RagRenderer(props: FeedRendererProps) {
  const { events } = props;
  const [showRaw, setShowRaw] = useState(true);
  const { query, rows } = useMemo(() => extractChunks(latestChunkEvent(events)), [events]);

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-white/[0.12] bg-[#0d0d0d] px-2 py-1.5 text-[11px]">
        <div className="flex items-center gap-2">
          <span className="text-slate-400">Latest query:</span>
          <span className="truncate font-mono text-slate-200">{query || '—'}</span>
          <button
            onClick={() => setShowRaw((v) => !v)}
            className="ml-auto rounded border border-white/[0.12] px-1.5 py-0.5 text-slate-300 hover:bg-white/[0.06]"
          >
            {showRaw ? 'hide raw stream' : 'show raw stream'}
          </button>
        </div>
        {rows.length > 0 && (
          <div className="mt-1 max-h-44 overflow-auto rounded bg-black/30">
            <table className="w-full font-mono text-[10px]">
              <thead className="sticky top-0 bg-[#0d0d0d] text-slate-500">
                <tr>
                  <th className="px-1 py-0.5 text-left">chunk_id</th>
                  <th className="px-1 py-0.5 text-left">doc</th>
                  <th className="px-1 py-0.5 text-left">score</th>
                  <th className="px-1 py-0.5 text-left">text</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 50).map((r, i) => (
                  <tr key={i} className="border-t border-white/[0.06] align-top">
                    <td className="px-1 py-0.5 text-cyan-300">{asStr(r.chunk_id)}</td>
                    <td className="px-1 py-0.5 text-slate-400">{asStr(r.doc)}</td>
                    <td className="px-1 py-0.5 text-amber-300">
                      {asNum(r.rerank_score ?? r.score ?? r.avg_pool_score)}
                    </td>
                    <td className="px-1 py-0.5 text-emerald-300">{asStr(r.text).slice(0, 400)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showRaw && (
        <div className="min-h-0 flex-1">
          <JsonLogRenderer {...props} />
        </div>
      )}
    </div>
  );
}

export default RagRenderer;
